import { assertEvent } from 'xstate5';

import { type Guard } from '../../types';
import { LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH } from '../../LocationSearchMachine.constants'; // prettier-ignore

// ─── Locations ───────────────────────────────────────────────────────────────

export const checkIfEnteredMinChars: Guard = ({ event }) => {
  assertEvent(event, 'SET_DELIVERY_SEARCH_STRING');

  return event.searchString.length >= LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH;
};

export const checkIfShouldFetchInitialLocationsByCurrentPosition: Guard = ({ context }) =>
  context.currentPosition !== undefined && !context.locations;

export const checkIfShouldFetchLocationsByCurrentPosition: Guard = ({ context }) =>
  context.currentPosition !== undefined;

export const checkIfAcceptableSearchQuery: Guard = ({ context }) => {
  const searchQuery = context.predictionsSearchQuery ?? '';

  return searchQuery.trim().length >= LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH;
};

/**
 * Reads the boolean output of the location-warning actor, forwarded via the
 * guard's `params` from the `onDone` transition. The actor may resolve to
 * `undefined`, which is treated the same as `false` (matches prior behavior).
 */
export const checkIfShouldShowLocationWarning: Guard<boolean | undefined> = (_, params) =>
  params === true;

// ─── Search Type ─────────────────────────────────────────────────────────────

export const checkIfPickupOrderChannel: Guard = ({ context }) =>
  context.locationSearchType === 'pickup';

export const checkIfDeliveryOrderChannel: Guard = ({ context }) =>
  context.locationSearchType === 'delivery';

export const checkIfOutpostOrderChannel: Guard = ({ context }) =>
  context.locationSearchType === 'outpost';

// ─── Recent And Nearby Locations ─────────────────────────────────────────────

export const checkIfHaveRecentOrNearbyLocations: Guard = ({ context }) => {
  const { locationSearchType, nearbyLocations } = context;

  const recentLocations = context.recentLocations ?? [];
  const nearbyPickupLocations =
    locationSearchType === 'pickup' ? (nearbyLocations?.pickup ?? []) : [];
  const nearbyOutpostLocations =
    locationSearchType === 'outpost' ? (nearbyLocations?.outpost ?? []) : [];

  return (
    recentLocations.length > 0 || nearbyPickupLocations.length + nearbyOutpostLocations.length > 0
  );
};

// ─── Delivery Location ───────────────────────────────────────────────────────

export const checkIfShouldGetDefaultDeliveryLocationOnEntry: Guard = ({ context }) =>
  !context.deliveryLocation || !context.deliveryLocation.restaurantSlug;

export const checkIfShouldAddNewDeliveryLocation: Guard = ({ context }) =>
  !context.deliveryLocation?.addressId;
